import React, { useState, useMemo, useCallback } from 'react';
import { useChatContext } from '../context/ChatContext';
import { IconButton, Button, Checkbox, InputBase, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import { ArrowBack, Delete } from '@mui/icons-material';

// Memoized chat item component to prevent unnecessary re-renders
const ChatHistoryItem = React.memo(({ 
  chat, 
  selected, 
  hovered, 
  selectionMode, 
  onItemClick, 
  onMouseEnter, 
  onMouseLeave, 
  onSelect, 
  onDelete 
}: {
  chat: any;
  selected: boolean;
  hovered: boolean;
  selectionMode: boolean;
  onItemClick: (id: string) => void;
  onMouseEnter: (id: string) => void;
  onMouseLeave: () => void;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
}) => {
  return (
    <div
      key={chat.id}
      className={`chat-history-item ${selected ? 'selected' : ''} ${hovered ? 'hovered' : ''}`}
      data-chat-id={chat.id}
      tabIndex={0}
      onClick={() => onItemClick(chat.id)}
      onMouseEnter={() => onMouseEnter(chat.id)}
      onMouseLeave={onMouseLeave}
    >
      <div className="chat-history-checkbox-container">
        {(selectionMode || selected || hovered) ? (
          <Checkbox
            checked={selected}
            onClick={e => {
              e.stopPropagation();
              onSelect(chat.id);
            }}
            size="small"
            sx={{
              backgroundColor: '#fff',
              borderRadius: '4px',
              transition: 'box-shadow 0.1s',
              padding: 0,
              '& .MuiSvgIcon-root': {
                backgroundColor: '#fff',
                borderRadius: '4px',
              },
            }}
          />
        ) : (
          <Checkbox checked={false} style={{ backgroundColor: '#fff', opacity: 0, pointerEvents: 'none', padding: 0 }} tabIndex={-1} />
        )}
      </div>
      <div className="chat-history-title">{chat.title || 'Untitled Chat'}</div>
      <div className="chat-history-date">Last message {formatTimeAgo(chat.timestamp)}</div>
      {!selectionMode && (
        <span
          className={`chat-history-delete-icon ${hovered ? 'visible' : ''}`}
          title="Delete chat"
          onClick={e => { e.stopPropagation(); onDelete(chat.id); }}
        >
          <Delete fontSize="small" />
        </span>
      )}
    </div>
  );
});

const ChatHistoryView: React.FC = () => {
  const {
    appState,
    loadChat,
    deleteChat,
    deleteMultipleChats,
    hideAllChatsView
  } = useChatContext();

  const [search, setSearch] = useState('');
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[] | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  // Memoize filtered and sorted chats to prevent recalculation on every render
  const { chats, total } = useMemo(() => {
    let filteredChats = appState.savedChats;
    const total = filteredChats.length;
    
    if (search.trim()) {
      const searchTerm = search.toLowerCase();
      filteredChats = filteredChats.filter(chat => 
        (chat.title || 'Untitled Chat').toLowerCase().includes(searchTerm)
      );
    }
    
    const sortedChats = [...filteredChats].sort((a, b) => 
      new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    );
    
    return { chats: sortedChats, total };
  }, [appState.savedChats, search]);

  // Memoized handlers to prevent function recreation on every render
  const handleSelect = useCallback((chatId: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(chatId)) {
      newSet.delete(chatId);
    } else {
      newSet.add(chatId);
    }
    setSelectedIds(newSet);
    if (newSet.size === 0) setSelectionMode(false);
  }, [selectedIds]);

  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === chats.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(chats.map(c => c.id)));
    }
  }, [selectedIds.size, chats]);

  const handleDeleteSelected = useCallback(() => {
    setPendingDeleteIds(Array.from(selectedIds));
    setDeleteDialogOpen(true);
  }, [selectedIds]);

  const handleDeleteSingle = useCallback((chatId: string) => {
    setPendingDeleteIds([chatId]);
    setDeleteDialogOpen(true);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (pendingDeleteIds) {
      setIsDeleting(true);
      if (pendingDeleteIds.length === 1) {
        // Use single delete for single chat
        await deleteChat(pendingDeleteIds[0]);
      } else {
        // Use bulk delete for multiple chats
        await deleteMultipleChats(pendingDeleteIds);
      }
      setSelectedIds(new Set());
      setSelectionMode(false);
      setIsDeleting(false);
    }
    setDeleteDialogOpen(false);
    setPendingDeleteIds(null);
  }, [pendingDeleteIds, deleteChat, deleteMultipleChats]);

  const cancelDelete = useCallback(() => {
    setDeleteDialogOpen(false);
    setPendingDeleteIds(null);
  }, []);

  const handleItemClick = useCallback((chatId: string) => {
    if (!selectionMode) {
      loadChat(chatId);
      hideAllChatsView();
    } else {
      handleSelect(chatId);
    }
  }, [selectionMode, loadChat, hideAllChatsView, handleSelect]);

  const handleItemSelect = useCallback((chatId: string) => {
    if (!selectionMode) {
      setSelectionMode(true);
      setSelectedIds(new Set([chatId]));
    } else {
      handleSelect(chatId);
    }
  }, [selectionMode, handleSelect]);

  const handleMouseEnter = useCallback((chatId: string) => {
    setHoveredId(chatId);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setHoveredId(null);
  }, []);

  // Memoize count message
  const countMsg = useMemo(() => {
    if (selectionMode) {
      return `${selectedIds.size} selected chats`;
    } else if (search.trim() && chats.length !== total) {
      return `Showing ${chats.length} of ${total} chats`;
    } else {
      return `You have ${total} previous chat${total === 1 ? '' : 's'}`;
    }
  }, [selectionMode, selectedIds.size, search, chats.length, total]);

  return (
    <>
      <style>{`
        .chat-history-item {
          background: #fff;
          border: 2px solid transparent;
          border-radius: 8px;
          padding: 14px 18px 10px 18px;
          margin-bottom: 0;
          display: flex;
          flex-direction: column;
          cursor: pointer;
          outline: none;
          position: relative;
          transition: background 0.1s ease, border-color 0.1s ease;
        }
        
        .chat-history-item:hover,
        .chat-history-item.hovered {
          background: #e6f7f2;
          border-color: var(--primary-color);
        }
        
        .chat-history-item.selected {
          background: #e6f7f2;
          border-color: var(--primary-color);
        }
        
        .chat-history-checkbox-container {
          position: absolute;
          left: -11px;
          top: 50%;
          transform: translateY(-50%);
          z-index: 2;
          display: flex;
          align-items: center;
          height: 24px;
        }
        
        .chat-history-title {
          font-size: 15px;
          font-weight: 600;
          color: var(--neutral-dark);
          margin-bottom: 3px;
          line-height: 1.3;
          padding-left: 18px;
        }
        
        .chat-history-date {
          font-size: 12px;
          color: #888;
          font-weight: 400;
          padding-left: 18px;
        }
        
        .chat-history-delete-icon {
          position: absolute;
          top: 8px;
          right: 10px;
          font-size: 16px;
          color: #A0A0A0;
          background: none;
          border-radius: 50%;
          padding: 2px;
          cursor: pointer;
          opacity: 0;
          visibility: hidden;
          transition: opacity 0.1s ease, visibility 0.1s ease;
          z-index: 3;
        }
        
        .chat-history-delete-icon.visible {
          opacity: 1;
          visibility: visible;
        }
        
        .chat-history-delete-icon:hover {
          color: #E1502F;
          background: rgba(225, 80, 47, 0.1);
        }
      `}</style>
      
      <div className="chat-history-view" style={{ width: '100%', maxWidth: 700, margin: '40px auto 0 auto', minHeight: 400, display: 'flex', flexDirection: 'column', alignItems: 'stretch', position: 'relative', height: 'calc(100vh - 60px)' }}>
        {isDeleting && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(255,255,255,0.7)',
            zIndex: 9999,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <img src="/images/belle-spinner.gif" alt="Loading..." style={{ width: 48, height: 48 }} />
            <div style={{ marginTop: 16, fontSize: 18, color: '#333' }}>Deleting chat{pendingDeleteIds && pendingDeleteIds.length > 1 ? 's' : ''}...</div>
          </div>
        )}
        <div className="chat-history-header" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <IconButton onClick={hideAllChatsView}><ArrowBack /></IconButton>
            <h2 style={{ margin: 0, marginLeft: 8 }}>Your chat history</h2>
          </div>
          <InputBase
            className="chat-history-search"
            placeholder="Search your chats..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            sx={{ width: '100%', fontSize: 14, margin: '12px 0 0 0', background: '#fafbfc', borderRadius: 2, border: '1px solid #eee', padding: '8px 12px' }}
          />
          <div className="chat-history-count-msg" style={{ minHeight: 60, display: 'flex', alignItems: 'center', width: '100%', margin: '12px 0 0 2px', height: 36 }}>
            <span>{countMsg}</span>
            {selectionMode ? (
              <div className="chat-history-controls" style={{ display: 'flex', alignItems: 'center', marginLeft: 'auto', gap: 10 }}>
                <Button size="small" onClick={handleSelectAll}>Select all</Button>
                <Button size="small" onClick={() => { setSelectionMode(false); setSelectedIds(new Set()); }}>Cancel</Button>
                <Button size="small" color="error" variant="contained" onClick={handleDeleteSelected}>Delete Selected</Button>
              </div>
            ) : (
              <a href="#" style={{ marginLeft: 10 }} onClick={e => { e.preventDefault(); setSelectionMode(true); }}>Select</a>
            )}
          </div>
        </div>
        <div className="chat-history-list" style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 6, marginTop: 0, flex: 1, overflowY: 'auto', paddingLeft: 12, paddingRight: 12 }}>
          {chats.length === 0 && <div className="chat-history-empty">No chats found.</div>}
          {chats.map(chat => (
            <ChatHistoryItem
              key={chat.id}
              chat={chat}
              selected={selectedIds.has(chat.id)}
              hovered={hoveredId === chat.id}
              selectionMode={selectionMode}
              onItemClick={handleItemClick}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onSelect={handleItemSelect}
              onDelete={handleDeleteSingle}
            />
          ))}
        </div>
        <Dialog open={deleteDialogOpen} onClose={cancelDelete}>
          <DialogTitle>Delete Chat{pendingDeleteIds && pendingDeleteIds.length > 1 ? 's' : ''}</DialogTitle>
          <DialogContent>
            {pendingDeleteIds && pendingDeleteIds.length > 1
              ? `Are you sure you want to delete ${pendingDeleteIds.length} selected chat(s)? This action cannot be undone.`
              : 'Are you sure you want to delete this chat? This action cannot be undone.'}
          </DialogContent>
          <DialogActions>
            <Button onClick={cancelDelete}>Cancel</Button>
            <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
          </DialogActions>
        </Dialog>
      </div>
    </>
  );
};

function formatTimeAgo(timestamp: string) {
  const now = new Date();
  const date = new Date(timestamp);
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${diffDays}d ago`;
}

export default ChatHistoryView; 